import { useState, useRef, useEffect } from "react";
import { Radio, Play, Pause, Volume2, VolumeX, SkipForward, Music, RefreshCw } from "lucide-react";

// Popular internet radio stations with reliable streams
const RADIO_STATIONS = [
  { name: "🎸 Classic Rock", url: "https://streaming.live365.com/a89267", genre: "rock" },
  { name: "🎵 Top 40 Hits", url: "https://stream.revma.ihrhls.com/zc1465", genre: "pop" },
  { name: "🎷 Smooth Jazz", url: "https://strm112.1.fm/smoothjazz_mobile_mp3", genre: "jazz" },
  { name: "🎹 Chill Lounge", url: "https://strm112.1.fm/chilloutlounge_mobile_mp3", genre: "chill" },
  { name: "🔥 Hip Hop Hits", url: "https://strm112.1.fm/hiphop_mobile_mp3", genre: "hiphop" },
  { name: "⚡ Club Dance", url: "https://strm112.1.fm/club_mobile_mp3", genre: "electronic" },
  { name: "🎻 Classical", url: "https://strm112.1.fm/classical_mobile_mp3", genre: "classical" },
  { name: "🤠 Country Roads", url: "https://strm112.1.fm/country_mobile_mp3", genre: "country" },
  { name: "🎺 Salsa Latin", url: "https://strm112.1.fm/salsa_mobile_mp3", genre: "latin" },
  { name: "🌙 Ambient Chill", url: "https://strm112.1.fm/ambient_mobile_mp3", genre: "ambient" },
  { name: "🤘 Heavy Metal", url: "https://strm112.1.fm/metal_mobile_mp3", genre: "metal" },
  { name: "🎤 80s Hits", url: "https://strm112.1.fm/80s_mobile_mp3", genre: "80s" },
  { name: "🕺 90s Dance", url: "https://strm112.1.fm/90s_mobile_mp3", genre: "90s" },
];

const InternetRadio = ({ roomId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStation, setCurrentStation] = useState(RADIO_STATIONS[0]);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [stationError, setStationError] = useState(false);
  const audioRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  // Rooms that should have internet radio
  const RADIO_ROOMS = [
    "staff-room",
    "music-room",
    "music-unsigned",
    "the-club",
    "music",
    "country-music-room",
    "metal-rock-room",
    "all-metal-room",
    "dance-room",
    "comedy-chat-room",
    "tiki-bar"
  ];

  // Internet radio is available in every room across the site.
  const hasRadio = true;

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Handle audio errors and auto-scan to next station
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      console.log("Station error, trying next...");
      setStationError(true);
      if (isScanning) {
        // Auto-scan to next station
        scanTimeoutRef.current = setTimeout(() => {
          nextStation();
        }, 1000);
      }
    };

    const handleCanPlay = () => {
      setStationError(false);
      setIsScanning(false);
    };

    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    };
  }, [isScanning, currentStation]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setStationError(false);
      audioRef.current.play().catch(e => {
        console.error("Playback error:", e);
        setStationError(true);
      });
      setIsPlaying(true);
    }
  };

  const changeStation = (station) => {
    setCurrentStation(station);
    setShowSelector(false);
    setStationError(false);
    if (audioRef.current) {
      audioRef.current.src = station.url;
      if (isPlaying) {
        audioRef.current.play().catch(e => {
          console.error("Playback error:", e);
          setStationError(true);
        });
      }
    }
  };

  const nextStation = () => {
    const currentIndex = RADIO_STATIONS.findIndex(s => s.name === currentStation.name);
    const nextIndex = (currentIndex + 1) % RADIO_STATIONS.length;
    changeStation(RADIO_STATIONS[nextIndex]);
  };

  const scanStations = () => {
    setIsScanning(true);
    setIsPlaying(true);
    nextStation();
  };

  if (!hasRadio) return null;

  return (
    <div className="bg-gradient-to-r from-purple-900 to-indigo-900 rounded-lg p-2 mb-2">
      <audio ref={audioRef} src={currentStation.url} preload="none" />
      
      <div className="flex items-center gap-2">
        {/* Radio Icon */}
        <Radio className={`w-5 h-5 ${isPlaying && !stationError ? 'text-green-400 animate-pulse' : stationError ? 'text-red-400' : 'text-gray-400'}`} />
        
        {/* Station Name / Selector */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowSelector(!showSelector)}
            className="text-white text-sm font-bold flex items-center gap-1 hover:text-yellow-300 transition-colors"
          >
            <Music className="w-3 h-3" />
            {currentStation.name}
            {isScanning && <span className="text-yellow-400 text-xs ml-1">(Scanning...)</span>}
          </button>
          
          {/* Station Dropdown */}
          {showSelector && (
            <div className="absolute bottom-full left-0 mb-1 bg-gray-900 rounded-lg border border-gray-700 shadow-xl z-50 w-48 max-h-48 overflow-y-auto">
              {RADIO_STATIONS.map(station => (
                <button
                  key={station.name}
                  onClick={() => changeStation(station)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-800 ${
                    currentStation.name === station.name ? 'bg-purple-800 text-white' : 'text-gray-300'
                  }`}
                >
                  {station.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className={`p-1.5 rounded ${isPlaying ? 'bg-green-600' : 'bg-gray-700'} text-white hover:opacity-80`}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          {/* Next Station */}
          <button
            onClick={nextStation}
            className="p-1.5 rounded bg-gray-700 text-white hover:bg-gray-600"
            title="Next Station"
          >
            <SkipForward className="w-4 h-4" />
          </button>

          {/* Auto Scan */}
          <button
            onClick={scanStations}
            className={`p-1.5 rounded ${isScanning ? 'bg-yellow-600 animate-spin' : 'bg-gray-700'} text-white hover:opacity-80`}
            title="Scan Stations"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {/* Mute */}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`p-1.5 rounded ${isMuted ? 'bg-red-600' : 'bg-gray-700'} text-white hover:opacity-80`}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          {/* Volume Slider */}
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-16 h-1 accent-purple-500"
            title={`Volume: ${Math.round(volume * 100)}%`}
          />
        </div>
      </div>

      {/* Now Playing indicator */}
      {isPlaying && !stationError && (
        <div className="text-xs text-purple-300 mt-1 flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          NOW PLAYING - {currentStation.genre.toUpperCase()}
        </div>
      )}
      
      {stationError && (
        <div className="text-xs text-red-300 mt-1 flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-red-400 rounded-full"></span>
          Station unavailable - Click ⏭ or 🔄 to try another
        </div>
      )}
    </div>
  );
};

export default InternetRadio;
